/**
 * Transform casing of keys within an object.
 *
 * Transform examples:
 * camelCase: twoWords
 * capitalCase: Two Words
 * constantCase: TWO_WORDS
 * dotCase: two.words
 * kebabCase: two-words
 * noCase: two words
 * pascalCase: TwoWords
 * pascalSnakeCase: Two_Words
 * pathCase: two/words
 * sentenceCase: Two words
 * snakeCase: two_words
 * trainCase: Two-Words
 */
import * as changeKeysUtils from 'change-case/keys';
import camelCase from 'voca/camel_case';
import vocaSnakeCase from 'voca/snake_case';
import vocaTitleCase from 'voca/title_case';

/**
 * Voca String manipulation exports
 */
export { default as capitalize } from 'voca/capitalize';
export { default as decapitalize } from 'voca/decapitalize';
export { default as kebabcase } from 'voca/kebab_case';
export { default as slugify } from 'voca/slugify';
export { default as camelCase } from 'voca/camel_case';
export const snakeCase = vocaSnakeCase;

export const transformKeys = {
  ...changeKeysUtils,
  lowerCase: (obj: Record<string, unknown>): Record<string, unknown> => {
    const lowerCasedObj: Record<string, unknown> = {};
    // For...in is faster here, and we don't need to worry about prototype pollution in this scenario.
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      lowerCasedObj[key.toLowerCase()] = obj[key];
    }

    return lowerCasedObj;
  },
};

/**
 * Convert any cased string into Title Case.
 */
export const titleCase = (str: string): string => {
  if (str.toUpperCase() === str) {
    return str;
  }
  // Make sure we can properly convert all casings.
  // Thought this was smoother than testing the string for each specific casing
  const camelCased = camelCase(str);
  return vocaTitleCase(camelCased.replaceAll(/([A-Z])/g, ' $1'));
};

/**
 * Convert string to CONSTANT_CASE aka SCREAMING_SNAKE_CASE !!!
 */
export const constantCase = (str: string): string =>
  snakeCase(str).toUpperCase();

/**
 * @description Masks a value, leaving 4 or a custom number of characters at the end. to replace all characters, pass in a negative value equal to the length of the string
 */
export const simpleValueMask = (
  value: string,
  num = 4,
  maskChar = '●',
): string => {
  if (!value) {
    return '';
  }
  return `${value}`.slice(-num).padStart(`${value}`.length, maskChar);
};

/**
 *
 * @description Converts a camelCase string to a label
 */
export const camelCaseToLabel = (value: string): string => {
  const result = value
    .replaceAll(/([A-Z]+)/g, ' $1')
    .replaceAll(/([A-Z])([A-Z])(?=[a-z])/g, '$1 $2');
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};

/**
 * Convert a string to a hexidecimal hash within a browser.
 * @param value The string value to convert.
 * @param algorithm The cryptographic algorithm to use.
 * @returns The hexidecimal hash.
 */
export const hash = async (
  value: string,
  algorithm: AlgorithmIdentifier = 'SHA-256',
): Promise<string> => {
  const utf8 = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest(algorithm, utf8);
  const hashHex = [...new Uint8Array(hashBuffer)]
    .map((bytes) => bytes.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};
